import React, { useState, useEffect, useRef } from 'react';
import {
  Bell, X, Check, CheckCheck, Calendar, MessageSquare,
  Target, Star, Users, AlertCircle, Clock, Trash2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import config from '../../config';
import './NotificationCenter.scss';

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  data?: any;
  is_read: boolean;
  read_at?: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  action_url?: string;
  created_at: string;
}

interface NotificationCenterProps {
  className?: string;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ className = '' }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const roleBase = user?.role_name === 'mentor' ? '/mentor'
    : user?.role_name === 'mentee' ? '/mentee' : '/admin';
  const goToAll = () => { setIsOpen(false); navigate(`${roleBase}/notifications`); };
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user && config.features.notifications) {
      loadNotificationCount();
      // Poll for new notifications based on config
      const interval = setInterval(loadNotificationCount, config.notifications.pollInterval);
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen, filter]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadNotificationCount = async () => {
    try {
      const response = await apiService.notifications.getCount();
      setUnreadCount(response.data.data.unread_count);
    } catch (error) {
      console.error('Error loading notification count:', error);
    }
  };

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const params = filter === 'unread' ? { unread: 'true' } : {};
      const response = await apiService.notifications.getAll(params);
      setNotifications(response.data.data);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: number) => {
    try {
      await apiService.notifications.markAsRead(notificationId);
      setNotifications(prevNotifications =>
        prevNotifications.map(notif =>
          notif.id === notificationId ? { ...notif, is_read: true } : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiService.notifications.markAllAsRead();
      setNotifications(prevNotifications =>
        prevNotifications.map(notif => ({ ...notif, is_read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (notificationId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await apiService.notifications.delete(notificationId);
      setNotifications(prevNotifications =>
        prevNotifications.filter(notif => notif.id !== notificationId)
      );
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    if (notification.action_url) {
      // Navegación SPA (sin recarga). Normaliza URLs absolutas del mismo dominio a ruta relativa.
      let target = notification.action_url;
      try {
        if (/^https?:\/\//i.test(target)) {
          const u = new URL(target);
          if (u.origin === window.location.origin) {
            target = `${u.pathname}${u.search}${u.hash}`;
          } else {
            window.location.href = target; // dominio externo: navegación normal
            setIsOpen(false);
            return;
          }
        }
      } catch { /* si no parsea, se usa tal cual abajo */ }
      setIsOpen(false);
      navigate(target);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'session_created':
      case 'session_updated':
      case 'session_reminder':
        return <Calendar size={16} />;
      case 'message_received':
        return <MessageSquare size={16} />;
      case 'goal_created':
      case 'goal_updated':
      case 'goal_completed':
        return <Target size={16} />;
      case 'evaluation_pending':
      case 'evaluation_completed':
        return <Star size={16} />;
      case 'relationship_created':
        return <Users size={16} />;
      default:
        return <Bell size={16} />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'notification--urgent';
      case 'high':
        return 'notification--high';
      case 'low':
        return 'notification--low';
      default:
        return '';
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Ahora mismo';
    if (diffInSeconds < 3600) return `Hace ${Math.floor(diffInSeconds / 60)} min`;
    if (diffInSeconds < 86400) return `Hace ${Math.floor(diffInSeconds / 3600)} horas`;
    if (diffInSeconds < 604800) return `Hace ${Math.floor(diffInSeconds / 86400)} días`;

    return date.toLocaleDateString('es-ES');
  };

  return (
    <div className={`notification-center ${className}`} ref={dropdownRef}>
      <button
        className="notification-bell"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notificaciones"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notificaciones</h3>
            <div className="notification-actions">
              {unreadCount > 0 && (
                <button
                  className="mark-all-read"
                  onClick={markAllAsRead}
                  title="Marcar todas como leídas"
                >
                  <CheckCheck size={16} />
                </button>
              )}
              <button
                className="close-notifications"
                onClick={() => setIsOpen(false)}
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="notification-filters">
            <button
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              Todas
            </button>
            <button
              className={`filter-btn ${filter === 'unread' ? 'active' : ''}`}
              onClick={() => setFilter('unread')}
            >
              No leídas ({unreadCount})
            </button>
          </div>

          <div className="notification-list">
            {loading ? (
              <div className="notification-loading">
                <div className="spinner"></div>
                <p>Cargando notificaciones...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                <Bell size={32} />
                <p>No tienes notificaciones</p>
              </div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  className={`notification-item ${
                    !notification.is_read ? 'unread' : ''
                  } ${getPriorityColor(notification.priority)}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon">
                    {getNotificationIcon(notification.type)}
                  </div>

                  <div className="notification-content">
                    <div className="notification-title">
                      {notification.title}
                      {!notification.is_read && (
                        <span className="unread-indicator"></span>
                      )}
                    </div>
                    <div className="notification-message">
                      {notification.message}
                    </div>
                    <div className="notification-meta">
                      <Clock size={12} />
                      <span>{formatTime(notification.created_at)}</span>
                      {notification.priority === 'urgent' && (
                        <>
                          <AlertCircle size={12} className="urgent-icon" />
                          <span className="urgent-label">Urgente</span>
                        </>
                      )}
                    </div>
                  </div>

                  <button
                    className="notification-delete"
                    onClick={(e) => deleteNotification(notification.id, e)}
                    title="Eliminar"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            )}
          </div>

          {notifications.length > 0 && (
            <div className="notification-footer">
              <button type="button" className="view-all" onClick={goToAll}>
                Ver todas las notificaciones
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;